class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.defaultOptions = {
      loop: true,
    };

    this.defaultThumbsOptions = {
      loop: false,
      spaceBetween: 20,
      slidesPerView: 4,
      watchSlidesProgress: true,
    };

    this.elements = {
      swiper: this.querySelector(".media-gallery"),
      prevButton: this.querySelector(".media-gallery__button--prev"),
      nextButton: this.querySelector(".media-gallery__button--next"),
      thumbs: this.querySelector(".media-gallery__thumbs"),
    };
    this.elements.mainWrapper = this.elements.swiper?.querySelector(
      ":scope > .media-gallery__wrapper",
    );
    this.elements.thumbsWrapper = this.elements.thumbs?.querySelector(
      ":scope > .media-gallery__wrapper",
    );
    this.productContainer = this.closest("[data-product-featured]");
    this.mainSlideTemplates = Array.from(this.elements.mainWrapper?.children ?? []).map((slide) =>
      slide.cloneNode(true),
    );
    this.thumbSlideTemplates = Array.from(this.elements.thumbsWrapper?.children ?? []).map(
      (slide) => slide.cloneNode(true),
    );
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this.productContainer,
        type: "change",
        callback: this.changeHandler.bind(this),
      },
      {
        el: document,
        type: "variant:change",
        callback: this.variantChangeHandler.bind(this),
      },
      {
        el: document,
        type: "DOMContentLoaded",
        callback: this.init.bind(this),
      },
    ];

    this.swiperOptions = {
      ...this.defaultOptions,
    };
    if (this.elements.swiper.dataset.galleryOptions) {
      this.swiperOptions = {
        ...this.defaultOptions,
        ...JSON.parse(this.elements.swiper.dataset.galleryOptions),
      };
    }

    if (this.elements.prevButton && this.elements.nextButton) {
      this.swiperOptions.navigation = {
        nextEl: this.elements.nextButton,
        prevEl: this.elements.prevButton,
      };
    }

    if (this.elements.thumbs) {
      this.thumbsSwiperOptions = {
        ...this.defaultThumbsOptions,
      };
      if (this.elements.thumbs.dataset.galleryOptions) {
        this.thumbsSwiperOptions = {
          ...this.defaultThumbsOptions,
          ...JSON.parse(this.elements.thumbs.dataset.galleryOptions),
        };
      }
      this.swiperOptions.thumbs = {
        swiper: this.elements.thumbs,
      };
    }
    this.mediaZoom = this.querySelector("media-zoom");
  }

  connectedCallback() {
    this.handleEventListeners();
    if (document.readyState !== "loading") {
      this.init();
    }
  }

  disconnectedCallback() {
    this.handleEventListeners("remove");
    this.destroySwipers();
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (!event.el) continue;
      if (type === "add") {
        event.el.addEventListener(event.type, event.callback);
      } else {
        event.el.removeEventListener(event.type, event.callback);
      }
    }
  }

  clickHandler(e) {
    if (e.target.classList.contains("media-gallery__play")) {
      this.videoSlide = e.target.closest(".media-gallery__slide-video");
      this.videoSlideMedia = this.videoSlide.querySelector("video");
      this.featuredVideo = document.querySelector("featured-video");
      if (this.videoSlideMedia) {
        this.playMedia(e);
      } else if (this.featuredVideo) {
        this.scrollToVideo();
      }
    }
  }

  init() {
    if (!this.elements.swiper || this.swiper) return;
    this.renderSlides();
    this.createSwipers();
  }

  createSwipers(preferredMediaId = "") {
    if (!this.elements.mainWrapper?.children.length) return;

    if (this.elements.thumbs && this.elements.thumbsWrapper?.children.length) {
      this.thumbsSwiper = new Swiper(this.elements.thumbs, this.thumbsSwiperOptions);
      this.swiperOptions.thumbs.swiper = this.thumbsSwiper;
    }
    this.swiper = new Swiper(this.elements.swiper, this.swiperOptions);
    this.swiper.on("slideChange", () => this.updateActiveMedia());

    const preferredIndex = Array.from(this.swiper.slides).findIndex(
      (slide) => slide.dataset.mediaId === preferredMediaId,
    );
    if (preferredIndex >= 0) {
      this.swiper.slideToLoop(preferredIndex, 0, false);
      this.thumbsSwiper?.slideTo(preferredIndex, 0, false);
    }
    this.updateActiveMedia();
  }

  destroySwipers() {
    if (this.swiper && !this.swiper.destroyed) {
      this.swiper.destroy(true, true);
    }
    if (this.thumbsSwiper && !this.thumbsSwiper.destroyed) {
      this.thumbsSwiper.destroy(true, true);
    }
    this.swiper = undefined;
    this.thumbsSwiper = undefined;
  }

  getVisibleSlides(templates, variant) {
    return templates
      .filter((slide) => {
        const optionPosition = Number(slide.dataset.mediaOptionPosition);
        const optionValue = slide.dataset.mediaOptionValue;

        if (!variant) return !slide.hidden;
        if (!optionPosition || !optionValue) return true;

        const selectedValue = String(variant.options[optionPosition - 1] ?? "").toLowerCase();
        return optionValue === selectedValue;
      })
      .map((slide) => {
        const visibleSlide = slide.cloneNode(true);
        visibleSlide.hidden = false;
        return visibleSlide;
      });
  }

  renderSlides(variant) {
    const mainSlides = this.getVisibleSlides(this.mainSlideTemplates, variant);
    const thumbSlides = this.getVisibleSlides(this.thumbSlideTemplates, variant);

    this.elements.mainWrapper?.replaceChildren(...mainSlides);
    this.elements.thumbsWrapper?.replaceChildren(...thumbSlides);
  }

  changeHandler(e) {
    if (!e.target.matches('select[name="id"]')) return;

    const variantsJson = this.productContainer?.querySelector(".pr_variants_json")?.textContent;
    if (!variantsJson) return;

    const variants = JSON.parse(variantsJson);
    const variant = variants.find(({ id }) => String(id) === e.target.value);
    this.updateVariantMedia(variant);
  }

  variantChangeHandler(e) {
    const variant = e.detail?.variant;
    const eventProduct = e.target.closest?.("[data-product-featured]");
    if (eventProduct !== this.productContainer) return;

    this.updateVariantMedia(variant);
  }

  updateVariantMedia(variant) {
    if (!variant?.options) return;

    this.updateSaleSticker(variant);
    this.destroySwipers();
    this.renderSlides(variant);
    this.createSwipers(String(variant.featured_media?.id ?? ""));
  }

  updateSaleSticker(variant) {
    const saleSticker = this.querySelector(".main-product__sale-sticker");
    if (!saleSticker) return;

    const price = Number(variant.price);
    const compareAtPrice = Number(variant.compare_at_price);
    saleSticker.hidden =
      !Number.isFinite(price) || !Number.isFinite(compareAtPrice) || compareAtPrice <= price;
  }

  updateActiveMedia() {
    const activeSlide = this.swiper?.slides[this.swiper.activeIndex];
    if (!activeSlide) return;

    if (this.activeVideo) {
      this.activeVideo.pause();
      this.activeVideo.currentTime = 0;
      this.videoSlide.classList.remove("video-playing");
    }
    if (!this.mediaZoom) return;

    this.mediaZoom.classList.toggle(
      "hide-zoom",
      Boolean(activeSlide.querySelector(".media-gallery__slide-video")),
    );
    const activeImage = activeSlide.querySelector(".luc-media img");
    const zoomImage = this.mediaZoom.querySelector("[data-zoom-image]");
    if (!activeImage || !zoomImage) return;

    const slideMediaURL = new URL(activeImage.src);
    slideMediaURL.searchParams.set("width", 2560);
    zoomImage.dataset.zoomImage = slideMediaURL;
  }

  playMedia() {
    this.videoSlide.classList.add("video-playing");
    this.activeVideo = this.videoSlideMedia;
    this.videoSlideMedia.play();
  }
  scrollToVideo() {
    const topBar = document.querySelector(".announcement-bar");
    const header = document.querySelector(".site-header");
    this.headerHeight = header?.offsetHeight + topBar?.offsetHeight;
    window.scrollTo({
      top: this.featuredVideo.offsetTop - this.headerHeight,
      behavior: "smooth",
    });
  }
}

customElements.define("media-gallery", MediaGallery);

class MediaZoom extends ModalDialog {
  constructor() {
    super();
    this.x = 50;
    this.y = 50;
    this.s = 100;
    this.cartridge = this.querySelector("[data-zoom-image]");

    this.eventCache = [];
    this.previousDiff = -1;
  }

  addEventListeners() {
    super.addEventListeners();
    this.cartridge.addEventListener("wheel", this.coarseHandler.bind(this));
    this.cartridge.addEventListener("mousedown", this.panStartHandler.bind(this));
    this.cartridge.addEventListener("mouseup", this.panEndHandler.bind(this));
    this.cartridge.addEventListener("mouseleave", this.panEndHandler.bind(this));
    this.cartridge.addEventListener("mousemove", this.panHandler.bind(this));

    this.cartridge.addEventListener("pointerdown", this.pointerDownHandler.bind(this));
    this.cartridge.addEventListener("pointermove", this.pointerMoveHandler.bind(this));
    this.cartridge.addEventListener("pointerup", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointercancel", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointerout", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointerleave", this.pointerUpHandler.bind(this));
  }

  openModal() {
    super.openModal();
    this.cartridgeWidth = this.cartridge.offsetWidth;
    this.cartridgeHeight = this.cartridge.offsetHeight;

    this.loadImage();
  }

  closeModal() {
    super.closeModal();
    this.x = 50;
    this.y = 50;
    this.s = 100;
    this.cartridge.removeAttribute("style");
  }

  clickHandler(e) {
    super.clickHandler(e);
  }

  setZoom(delta) {
    this.s -= delta;
    this.s = Math.min(Math.max(this.s, 100), 200);
    if (this.cartridgeWidth > this.cartridgeHeight) {
      this.cartridge.style.backgroundSize = `auto ${this.s}%`;
    } else if (this.cartridgeWidth < this.cartridgeHeight) {
      this.cartridge.style.backgroundSize = `${this.s}% auto`;
    } else {
      this.cartridge.style.backgroundSize = `${this.s}% ${this.s}%`;
    }
  }

  coarseHandler(e) {
    e.preventDefault();
    if (e.type === "wheel") {
      this.setZoom(e.deltaY);
    }
  }

  panStartHandler(e) {
    if (
      typeof this.y == "undefined" ||
      typeof this.x == "undefined" ||
      typeof this.s == "undefined"
    ) {
      this.x = 50;
      this.y = 50;
      this.s = 100;
    }
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.cartridge.classList.add("panning");
    this.mouseDown = true;
  }

  panEndHandler(e) {
    this.cartridge.classList.remove("panning");
    this.x = this.deltaX;
    this.y = this.deltaY;
    this.mouseDown = false;
  }

  panHandler(e) {
    if (!this.mouseDown) return;
    const deltaX = e.x - this.startX;
    const deltaY = e.y - this.startY;
    const deltaXPercentage = (deltaX / this.cartridgeWidth) * 100;
    const deltaYPercentage = (deltaY / this.cartridgeHeight) * 100;
    this.deltaX = Math.min(Math.max(this.x - deltaXPercentage * (this.s / 100), 0), 100);
    this.deltaY = Math.min(Math.max(this.y - deltaYPercentage * (this.s / 100), 0), 100);
    this.cartridge.style.backgroundPosition = `${this.deltaX}% ${this.deltaY}%`;
  }

  pointerDownHandler(e) {
    this.eventCache.push(e);
    if (this.eventCache.length === 1) {
      this.panStartHandler(e);
    } else {
      this.panEndHandler(e);
    }
  }

  pointerMoveHandler(e) {
    const index = this.eventCache.findIndex((cachedEvent) => cachedEvent.pointerId === e.pointerId);
    this.eventCache[index] = e;

    if (this.eventCache.length === 2) {
      const currentDiff = Math.abs(this.eventCache[0].clientX - this.eventCache[1].clientX);

      if (this.previousDiff > 0) {
        const delta = this.previousDiff - currentDiff;
        this.setZoom(delta);
      }

      this.previousDiff = currentDiff;
    } else {
      this.panHandler(e);
    }
  }

  pointerUpHandler(e) {
    const index = this.eventCache.findIndex((cachedEvent) => cachedEvent.pointerId === e.pointerId);
    this.eventCache.splice(index, 1);
    if (this.eventCache.length < 2) {
      this.previousDiff = -1;
    }
    if (this.eventCache.length <= 0) {
      this.x = this.deltaX;
      this.y = this.deltaY;
      this.mouseDown = false;
    }
  }

  loadImage() {
    this.cartridge.style.backgroundImage = `url(${this.cartridge.dataset.zoomImage})`;
  }
}

customElements.define("media-zoom", MediaZoom);
